import { Tab } from "@headlessui/react";
import { KeyIcon, LockClosedIcon } from "@heroicons/react/24/outline";
import { createColumnHelper } from "@tanstack/react-table";
import { useAtom } from "jotai";
import { useCallback, useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import {
    type DataImport,
    DataImportListImportTypeEnum,
    type DataImportListRequest,
    DataImportListStatusEnum,
    type OAuthApplication,
} from "../../apiClient/generated";
import { useUser } from "../../auth";
import { CopyButton } from "../../components/DataImports/CopyButton";
import { DataImportDetailPanel } from "../../components/DataImports/DataImportDetailPanel";
import {
    actionText,
    dataTypeLabel,
    formatDuration,
    humanizeCode,
    importTypeLabel,
    isTerminal,
    providerLabel,
    secondsBetween,
    stateLabel,
} from "../../components/DataImports/pipeline";
import {
    StatusBadge,
    StatusDot,
    statusTone,
} from "../../components/DataImports/StatusBadge";
import { DataTableV3 } from "../../components/DataTable/DataTableV3";
import { tableStateFamily } from "../../components/DataTable/state";
import { useAccountsApiClient, useDataImportApiClient } from "../../hooks";
import { Card } from "../../ui/Card";
import { formatDateTime } from "../../utils/dates";
import { ROUTES } from "..";

// The open import is kept in the URL, so it can be linked to.
const SELECTED_IMPORT_PARAM = "import";

const Muted = () => <span className="text-ae-slate-600">—</span>;

/** How a running import's current step is doing: its action, and for how long. */
const CurrentStep = ({ dataImport }: { dataImport: DataImport }) => {
    // Nothing to show once the pipeline has ended, or for legacy imports
    if (!dataImport.state || isTerminal(dataImport.state)) {
        return <Muted />;
    }
    const action = actionText(dataImport.actionStatus, true);
    // Only read once there is a state: the generated client turns a null
    // state_entered_at into 1970.
    const since = dataImport.stateEnteredAt
        ? `for ${formatDuration(secondsBetween(dataImport.stateEnteredAt, new Date()))}`
        : null;
    // The step itself is in the State column
    return (
        <div className="text-sm">
            {[action, since].filter(Boolean).join(" · ")}
        </div>
    );
};

/**
 * The import's state machine state, colored by its status. Imports from before
 * the state machine have no state, so they show their status.
 */
const StateBadge = ({ dataImport }: { dataImport: DataImport }) =>
    dataImport.state ? (
        <span
            className="inline-flex items-center gap-2 whitespace-nowrap"
            title={dataImport.state}
        >
            <StatusDot tone={statusTone(dataImport.status)} />
            {stateLabel(dataImport.state)}
        </span>
    ) : (
        <StatusBadge status={dataImport.status} />
    );

const columnHelper = createColumnHelper<DataImport>();
const columns = [
    columnHelper.accessor("createdAt", {
        id: "createdAt",
        cell: (info) => formatDateTime(info.getValue()),
        header: () => "Created",
        enableSorting: true,
    }),
    columnHelper.accessor("importType", {
        id: "importType",
        header: () => "Type",
        cell: (info) => {
            const dataType = dataTypeLabel(info.row.original.importParams);
            return (
                <div>
                    <div>{importTypeLabel(info.getValue())}</div>
                    {dataType && (
                        <div className="text-xs text-ae-slate-600">
                            {dataType}
                        </div>
                    )}
                </div>
            );
        },
        enableSorting: true,
    }),
    columnHelper.display({
        id: "provider",
        header: () => "Provider",
        cell: (info) =>
            providerLabel(info.row.original.importParams) ?? <Muted />,
        enableSorting: false,
    }),
    columnHelper.display({
        id: "state",
        cell: (info) => <StateBadge dataImport={info.row.original} />,
        header: () => "State",
        // state is computed by the serializer, so the API can't order by it
        enableSorting: false,
    }),
    columnHelper.display({
        id: "step",
        header: () => "Current step",
        cell: (info) => <CurrentStep dataImport={info.row.original} />,
        enableSorting: false,
    }),
];

const STATUS_OPTIONS = Object.values(DataImportListStatusEnum);
const IMPORT_TYPE_OPTIONS = Object.values(DataImportListImportTypeEnum);

const FilterSelect = <T extends string>({
    label,
    value,
    options,
    optionLabel,
    onChange,
}: {
    label: string;
    value: T | "";
    options: T[];
    optionLabel: (option: T) => string;
    onChange: (value: T | "") => void;
}) => (
    <label className="flex items-center gap-2">
        {label}
        <select
            className="h-8 rounded border-neutral-300 py-1 text-sm"
            value={value}
            onChange={(e) => onChange(e.target.value as T | "")}
        >
            <option value="">All</option>
            {options.map((option) => (
                <option key={option} value={option}>
                    {optionLabel(option)}
                </option>
            ))}
        </select>
    </label>
);

const ManageAPIKeyCard: React.FC = () => {
    const accountsApiClient = useAccountsApiClient();

    // --- Legacy API key state ---
    const [token, setToken] = useState<string | null>(null);

    useEffect(() => {
        accountsApiClient
            .accountsTokenRetrieve()
            .then((response) => setToken(response.token))
            .catch(() => setToken(null));
    }, [accountsApiClient]);

    const regenerateToken = useCallback(() => {
        accountsApiClient
            .accountsTokenCreate()
            .then((response) => setToken(response.token))
            .catch(() => setToken(null));
    }, [accountsApiClient]);

    // --- OAuth credentials state ---
    const [oauthApp, setOauthApp] = useState<
        OAuthApplication | null | undefined
    >(undefined);
    const [newSecret, setNewSecret] = useState<string | null>(null);
    const [appName, setAppName] = useState("");
    const [oauthLoading, setOauthLoading] = useState(false);

    useEffect(() => {
        accountsApiClient
            .accountsOauthCredentialsRetrieve()
            .then((app) => setOauthApp(app))
            .catch(() => setOauthApp(null));
    }, [accountsApiClient]);

    const createCredentials = useCallback(() => {
        if (!appName.trim()) return;
        setOauthLoading(true);
        accountsApiClient
            .accountsOauthCredentialsCreate({
                oAuthApplicationCreateRequest: { name: appName.trim() },
            })
            .then((result) => {
                setOauthApp({
                    name: result.name,
                    clientId: result.clientId,
                    created: result.created,
                });
                setNewSecret(result.clientSecret);
                setAppName("");
            })
            .finally(() => setOauthLoading(false));
    }, [accountsApiClient, appName]);

    const regenerateSecret = useCallback(() => {
        setOauthLoading(true);
        accountsApiClient
            .accountsOauthCredentialsRegenerateSecretCreate()
            .then((result) => {
                setNewSecret(result.clientSecret);
            })
            .finally(() => setOauthLoading(false));
    }, [accountsApiClient]);

    const tabClass = ({ selected }: { selected: boolean }) =>
        `flex items-center py-1 px-2 rounded-lg ${
            selected ? "font-bold" : "hover:text-slate-100 hover:bg-slate-800"
        }`;

    return (
        <Card>
            <Tab.Group>
                <div className="flex justify-between py-4 px-10 items-center">
                    <h1 className="text-xl">API Keys</h1>
                    <Tab.List className="flex space-x-4">
                        <Tab className={tabClass}>
                            <LockClosedIcon className="w-5 h-5 mr-1" />
                            API Key
                        </Tab>
                        <Tab className={tabClass}>
                            <KeyIcon className="w-5 h-5 mr-1" />
                            OAuth Credentials
                        </Tab>
                    </Tab.List>
                </div>
                <hr />

                <Tab.Panels>
                    {/* --- Legacy API Key tab --- */}
                    <Tab.Panel>
                        <div className="px-10 py-6">
                            <h2 className="font-bold text-lg">API Key</h2>
                            <p className="mt-2">
                                You can use this page to retrieve API keys to
                                use in data pipelines.
                            </p>
                            <p className="mt-2">
                                Regenerating the API key revokes previous keys.
                            </p>
                            <div className="flex mt-2">
                                <input
                                    type="text"
                                    readOnly={true}
                                    placeholder="No API key"
                                    className="rounded-lg w-96"
                                    value={token || ""}
                                />
                                <button
                                    className="ml-3 px-4 rounded-lg italic hover:bg-slate-700 hover:text-white"
                                    onClick={regenerateToken}
                                >
                                    Regenerate
                                </button>
                            </div>
                        </div>
                    </Tab.Panel>

                    {/* --- OAuth Credentials tab --- */}
                    <Tab.Panel>
                        <div className="px-10 py-6">
                            <h2 className="font-bold text-lg">
                                OAuth Credentials
                            </h2>
                            <p className="mt-2">
                                Use these credentials with the{" "}
                                <code>client_credentials</code> OAuth flow in
                                data pipeline scripts.
                            </p>

                            {oauthApp === undefined && (
                                <p className="mt-4 text-slate-400">Loading…</p>
                            )}

                            {oauthApp === null && (
                                <div className="mt-4">
                                    <p className="mb-2">
                                        No credentials yet. Enter an application
                                        name to create them.
                                    </p>
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="text"
                                            placeholder="Application name"
                                            className="rounded-lg w-72"
                                            value={appName}
                                            onChange={(e) =>
                                                setAppName(e.target.value)
                                            }
                                            onKeyDown={(e) =>
                                                e.key === "Enter" &&
                                                createCredentials()
                                            }
                                        />
                                        <button
                                            className="px-4 py-2 rounded-lg bg-slate-700 text-white hover:bg-slate-600 disabled:opacity-50"
                                            onClick={createCredentials}
                                            disabled={
                                                oauthLoading || !appName.trim()
                                            }
                                        >
                                            Create
                                        </button>
                                    </div>
                                </div>
                            )}

                            {oauthApp !== null && oauthApp !== undefined && (
                                <div className="mt-4 space-y-3">
                                    <div>
                                        <span className="text-sm text-slate-400">
                                            Application
                                        </span>
                                        <div className="font-mono mt-0.5">
                                            {oauthApp.name}
                                        </div>
                                    </div>
                                    <div>
                                        <span className="text-sm text-slate-400">
                                            Client ID
                                        </span>
                                        <div className="flex items-center font-mono mt-0.5">
                                            <span>{oauthApp.clientId}</span>
                                            <CopyButton
                                                value={oauthApp.clientId}
                                            />
                                        </div>
                                    </div>

                                    {newSecret ? (
                                        <div className="rounded-lg border border-yellow-500 bg-yellow-500/10 p-4">
                                            <p className="text-sm font-semibold text-yellow-400 mb-2">
                                                Copy your client secret now — it
                                                will not be shown again.
                                            </p>
                                            <div className="flex items-center font-mono break-all">
                                                <span>{newSecret}</span>
                                                <CopyButton value={newSecret} />
                                            </div>
                                            <button
                                                className="mt-3 text-sm underline text-slate-400 hover:text-white"
                                                onClick={() =>
                                                    setNewSecret(null)
                                                }
                                            >
                                                I've saved it, dismiss
                                            </button>
                                        </div>
                                    ) : (
                                        <div>
                                            <span className="text-sm text-slate-400">
                                                Client Secret
                                            </span>
                                            <div className="font-mono mt-0.5 text-slate-400">
                                                ••••••••••••••••
                                            </div>
                                        </div>
                                    )}

                                    <button
                                        className="px-4 py-2 rounded-lg border border-slate-600 hover:bg-slate-700 hover:text-white disabled:opacity-50"
                                        onClick={regenerateSecret}
                                        disabled={oauthLoading}
                                    >
                                        Regenerate Secret
                                    </button>
                                </div>
                            )}
                        </div>
                    </Tab.Panel>
                </Tab.Panels>
            </Tab.Group>
        </Card>
    );
};

const TABLE_NAME = "dataImport";

interface DataImportTableCardProps {
    selectedId: string | null;
    onSelect: (dataImportId: string) => void;
}

const DataImportTableCard = ({
    selectedId,
    onSelect,
}: DataImportTableCardProps) => {
    const apiClient = useDataImportApiClient();
    const [tableState, setTableState] = useAtom(
        tableStateFamily({ tableId: TABLE_NAME }),
    );

    const setFilter = (key: "status" | "importType", value: string) =>
        setTableState((ts) => ({
            ...ts,
            filters: { ...ts.filters, [key]: value || undefined },
            // Changing a filter returns to the first page.
            pagination: { ...ts.pagination, pageIndex: 0 },
        }));

    const fetchFunction = async (props: DataImportListRequest) => {
        return await apiClient.dataImportList(props);
    };

    return (
        <Card>
            <div className="mx-4 mb-3 flex flex-wrap items-center gap-4 text-sm">
                <FilterSelect
                    label="Status"
                    value={
                        (tableState.filters
                            .status as DataImportListStatusEnum) ?? ""
                    }
                    options={STATUS_OPTIONS}
                    optionLabel={humanizeCode}
                    onChange={(value) => setFilter("status", value)}
                />
                <FilterSelect
                    label="Type"
                    value={
                        (tableState.filters
                            .importType as DataImportListImportTypeEnum) ?? ""
                    }
                    options={IMPORT_TYPE_OPTIONS}
                    optionLabel={importTypeLabel}
                    onChange={(value) => setFilter("importType", value)}
                />
            </div>
            <hr />
            <DataTableV3
                dataName={TABLE_NAME}
                columns={columns}
                extraFilters={{}}
                fetchFunction={fetchFunction}
                sortable={true}
                filterable={false}
                onClickRow={onSelect}
                activeRowId={selectedId ?? undefined}
            />
        </Card>
    );
};

export const AdminDataImportPage = () => {
    const isStaff = useUser().isStaff;
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedId = searchParams.get(SELECTED_IMPORT_PARAM);

    const selectImport = useCallback(
        (dataImportId: string | null) =>
            setSearchParams(
                (params) => {
                    const next = new URLSearchParams(params);
                    if (dataImportId) {
                        next.set(SELECTED_IMPORT_PARAM, dataImportId);
                    } else {
                        next.delete(SELECTED_IMPORT_PARAM);
                    }
                    return next;
                },
                { replace: true },
            ),
        [setSearchParams],
    );

    if (!isStaff) {
        return <Navigate to={ROUTES.ROOT} />;
    }

    return (
        <div className="container mx-auto px-6 pb-6">
            <h1 className="mt-6 text-2xl">Manage Data Imports</h1>
            <ManageAPIKeyCard />
            <div className="grid grid-cols-1 gap-x-6 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
                <DataImportTableCard
                    selectedId={selectedId}
                    onSelect={selectImport}
                />
                {/* Stacked above the table on narrow screens, so it's seen */}
                <aside
                    aria-label="Data import detail"
                    className={`self-start xl:sticky xl:top-0 ${
                        selectedId
                            ? "order-first xl:order-none"
                            : "hidden xl:block"
                    }`}
                >
                    <Card>
                        <div className="xl:max-h-[calc(100vh-5rem)] overflow-y-auto">
                            {selectedId ? (
                                <DataImportDetailPanel
                                    key={selectedId}
                                    dataImportId={selectedId}
                                    onClose={() => selectImport(null)}
                                />
                            ) : (
                                <p className="text-sm text-ae-slate-600">
                                    Select an import to see its pipeline steps,
                                    errors and parameters.
                                </p>
                            )}
                        </div>
                    </Card>
                </aside>
            </div>
        </div>
    );
};
